/**
 * Turn any JSON body returned by our FastAPI backend into a single
 * human-readable string. Handles three common shapes:
 *
 * 1. `{detail: "some message"}` — HTTPException(detail=str). Return as-is.
 * 2. `{detail: [{loc:[...], msg:"...", type:"..."}, ...]}` — Pydantic
 *    validation. Render each item as "<field>: <msg>" joined with " · ".
 * 3. Anything else (no detail, network error, etc.) — return null so the
 *    caller can pick its own fallback string.
 *
 * Previously we did `err.detail || 'Failed to X'` which rendered
 * "[object Object]" on Pydantic errors. This helper fixes that everywhere.
 */
export function formatApiError(errObj) {
  if (!errObj || typeof errObj !== 'object') return null;
  const d = errObj.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d) && d.length > 0) {
    return d.map((entry) => {
      if (!entry || typeof entry !== 'object') return String(entry);
      const loc = Array.isArray(entry.loc) ? entry.loc.filter(seg => seg !== 'body').join('.') : '';
      const msg = entry.msg || entry.message || 'Invalid';
      return loc ? `${loc}: ${msg}` : msg;
    }).join(' · ');
  }
  if (d && typeof d === 'object' && (d.msg || d.message)) {
    return d.msg || d.message;
  }
  return null;
}

/**
 * Convenience: fetch a response's JSON error body and return a nice string.
 * Falls back to `fallback` if the response can't be parsed.
 */
export async function extractApiError(response, fallback = 'Request failed') {
  try {
    const body = await response.json();
    return formatApiError(body) || fallback;
  } catch {
    return fallback;
  }
}

export default formatApiError;
